import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Une case du groupe. `node` est l'élément DÉJÀ passé par le helper `F()` de la
 * fiche : il vaut `null` quand le rôle ne voit pas le champ, ce qui permet au
 * groupe de s'adapter tout seul aux permissions.
 */
export interface FieldGroupUnit {
  node: ReactNode;
  /** Poids flex de la case dans la ligne (défaut 1). */
  weight?: number;
  /**
   * Largeur FIXE en pixels, prioritaire sur `weight`.
   *
   * Les poids sont proportionnels à la largeur du groupe : deux groupes de
   * portées différentes donnaient des cases de tailles différentes pour le même
   * rôle. « Disponible » mesurait 138 px dans Cave et 96 px dans Parking. Les
   * cases qui jouent le même rôle d'un groupe à l'autre se figent donc.
   */
  basis?: number;
  /** Petit mot rendu AVANT la case (« sur » entre étage et étages total). */
  before?: string;
  /** Clé React stable. */
  key: string;
}

interface FieldGroupProps {
  /** Libellé unique qui porte le sens des cases (« Adresse », « Pièces »). */
  legend: string;
  units: FieldGroupUnit[];
  className?: string;
}

/**
 * Regroupe plusieurs champs qui répondent à UNE MÊME question sous un libellé
 * unique, chaque case gardant un micro-libellé sous elle.
 *
 * Motivation (chantier mise en page 2026-08-08) : « Nb chambres », « Nb salles
 * de bains », « Nb douches », « Nb toilettes » sont quatre libellés longs pour
 * quatre valeurs d'un caractère. Les mettre dans une grille dense obligeait à
 * les abréger (« Dch. », « Cuis. ») — un recul déguisé en optimisation. Le
 * regroupement SUPPRIME le libellé redondant au lieu de le mutiler.
 *
 * C'est le motif `fieldset`/`legend` recommandé par le design system GOV.UK,
 * qui cite explicitement le cas de l'adresse et l'applique à son champ date
 * (jour, mois, année sous une seule légende).
 *
 * Permissions : les cases dont `node` est `null` sont retirées ; si toutes le
 * sont, le groupe entier ne rend rien.
 */
export function FieldGroup({ legend, units, className }: FieldGroupProps) {
  const visible = units.filter((u) => u.node !== null && u.node !== undefined && u.node !== false);
  if (visible.length === 0) return null;

  return (
    <fieldset className={cn("flex flex-col min-w-0 border-0 p-0 m-0", className)}>
      <legend className="text-xs font-semibold tracking-wider uppercase text-text-secondary px-3 mb-1 p-0">
        {legend}
      </legend>
      <div className="flex gap-1.5 items-start">
        {visible.map((u) => (
          <div key={u.key} className="contents">
            {u.before && (
              <span className="self-center -mt-4 text-xs text-text-muted whitespace-nowrap">
                {u.before}
              </span>
            )}
            <div
              className="flex flex-col min-w-0"
              style={u.basis ? { flex: `0 0 ${u.basis}px` } : { flex: `${u.weight ?? 1} 1 0` }}
            >
              {u.node}
            </div>
          </div>
        ))}
      </div>
    </fieldset>
  );
}
